import type { ColumnDef } from '@tanstack/react-table';
import { Eye } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

import { useAuth } from '@/feature/auth/hooks/useAuth';
import { organizationService } from '@/feature/organization/services/organizationService';
import type { IOrganization } from '@/feature/organization/types/organization';
import DateRangeFilter from '@/shared/components/data-display/DateRangeFilter';
import { TanStackTable } from '@/shared/components/data-display/TanStackTable';
import SearchableSelect from '@/shared/components/forms/SearchableSelect';
import Button from '@/shared/components/ui/Button';
import { useServerListTableState } from '@/shared/hooks/useServerListTableState';
import { tokenStorage } from '@/shared/utils/tokenStorage';

import { tokenUsageService } from '../../feature/tokenUsage/services/tokenUsageService';
import type { ITokenUsageQuery } from '../../feature/tokenUsage/types/tokenUsage';
import { ROUTES } from '../../routes/routeConstants';

type TimeRangeKey = 'last_7_days' | 'last_30_days' | 'last_90_days' | 'custom';

const TOKEN_USAGE_SORT_FIELD_MAP: Partial<Record<string, string>> = {
  orgName: 'org_name',
  industry: 'industry',
  users: 'users',
  projects: 'projects',
  avgUsage: 'avg_usage',
  tokenUsage: 'token_usage',
};

interface ITokenUsageRow {
  orgId: string;
  orgName: string;
  industry: string;
  users?: number;
  projects?: number;
  avgUsage: number;
  totalTokensUsed: number;
  tokenLimit?: number;
}

interface ITokenUsageFiltersForm {
  dateFrom: string;
  dateTo: string;
}

function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return 0;
    }
    const parsed = Number(trimmed.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function getStringField(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim() !== '') {
      return v.trim();
    }
  }
  return '';
}

function getNumberField(obj: Record<string, unknown>, keys: string[]): number {
  for (const k of keys) {
    const v = obj[k];
    const n = toNumber(v);
    if (n !== 0) {
      return n;
    }
  }
  return 0;
}

function formatCompactNumber(n: number): string {
  if (!Number.isFinite(n)) {
    return '—';
  }
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) {
    return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (abs >= 1_000_000) {
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (abs >= 1_000) {
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  }
  return String(Math.round(n));
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) {
    return 0;
  }
  return Math.max(0, Math.min(1, v));
}

function getUsageColorClass(pct: number): string {
  if (pct >= 0.9) {
    return 'bg-red-500';
  }
  if (pct >= 0.65) {
    return 'bg-amber-500';
  }
  return 'bg-emerald-500';
}

function mapOrgToFallbackRow(org: IOrganization): ITokenUsageRow {
  const anyOrg = org as unknown as Record<string, unknown>;
  const orgId = String((anyOrg.id as string | number | undefined) ?? '');
  const orgName =
    getStringField(anyOrg, ['org_name', 'organizationName', 'name']) || `Org ${orgId || '—'}`;
  const industry = getStringField(anyOrg, ['industry']) || '—';
  const users = Array.isArray((anyOrg.users as unknown[] | undefined) ?? null)
    ? (anyOrg.users as unknown[]).length
    : getNumberField(anyOrg, ['users', 'user_count', 'users_count']);
  const projects = getNumberField(anyOrg, ['projects', 'project_count', 'projects_count']);
  const totalTokensUsed = getNumberField(anyOrg, [
    'total_tokens_used',
    'tokens_used',
    'token_usage',
    'tokenUsage',
  ]);
  const avgUsage = getNumberField(anyOrg, [
    'avg_usage',
    'avgUsage',
    'avg_daily_tokens',
    'avgDailyTokens',
  ]);
  const tokenLimit = getNumberField(anyOrg, ['token_limit', 'tokenLimit', 'tokens_limit']);
  return {
    orgId,
    orgName,
    industry,
    users: Number.isFinite(users) ? users : undefined,
    projects: Number.isFinite(projects) ? projects : undefined,
    avgUsage,
    totalTokensUsed,
    tokenLimit: tokenLimit || undefined,
  };
}

const TokenUsagePage = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ITokenUsageRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [industry, setIndustry] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<TimeRangeKey>('last_7_days');
  /** Custom range used for API calls (set only when user clicks Search). */
  const [appliedCustomRange, setAppliedCustomRange] = useState<{
    from: string;
    to: string;
  } | null>(null);
  const [apiSummary, setApiSummary] = useState<{
    totalUsage: number;
    activeOrgs: number;
    avgUsagePerOrg: number;
    topOrgName: string;
    topOrgTokens: number;
  } | null>(null);

  const { listParams, page, setPage, pageSize, search, setSearch, onSortChange } =
    useServerListTableState({
      pageSize: 10,
      initialSort: { columnKey: 'tokenUsage', direction: 'desc' },
      sortFieldByColumnKey: TOKEN_USAGE_SORT_FIELD_MAP,
    });

  const { register, watch, setValue, formState } = useForm<ITokenUsageFiltersForm>({
    defaultValues: { dateFrom: '', dateTo: '' },
    mode: 'onChange',
  });

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  const deriveFromTo = useCallback((): { from: number; to: number; label: string } | null => {
    const now = new Date();
    if (timeRange === 'custom') {
      if (!appliedCustomRange?.from || !appliedCustomRange?.to) {
        return null;
      }
      const from = new Date(appliedCustomRange.from).getTime();
      const to = new Date(appliedCustomRange.to).getTime();
      const label =
        appliedCustomRange.from === appliedCustomRange.to
          ? appliedCustomRange.from
          : `${appliedCustomRange.from} - ${appliedCustomRange.to}`;
      return { from, to, label };
    }
    let days = 7;
    if (timeRange === 'last_30_days') {
      days = 30;
    } else if (timeRange === 'last_90_days') {
      days = 90;
    }
    const to = now.getTime();
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000).getTime();
    const fromStr = new Date(from).toISOString().split('T')[0] || '';
    const toStr = new Date(to).toISOString().split('T')[0] || '';
    return { from, to, label: `${fromStr} - ${toStr}` };
  }, [timeRange, appliedCustomRange]);

  const selectedRangeLabel = useMemo(() => deriveFromTo()?.label ?? '', [deriveFromTo]);

  const fetchData = useCallback(async () => {
    if (authLoading) {
      return;
    }
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const range = deriveFromTo();
    if (!range) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { from, to } = range;
      const pageNum = Math.floor((listParams.offset ?? 0) / (listParams.limit ?? 10)) + 1;

      const query: ITokenUsageQuery = {
        fromDate: from,
        toDate: to,
        sort_by: listParams.sort_by || '-token_usage',
        page: pageNum,
        page_size: listParams.limit,
        search: listParams.search,
      };

      const summaryResp = await tokenUsageService.getTokenUsageSummary(query);
      if (summaryResp) {
        const mapped: ITokenUsageRow[] = summaryResp.organizations
          .filter((o) => (industry === 'all' ? true : (o.industry || '—') === industry))
          .map((o) => ({
            orgId: String(o.id),
            orgName: o.org_name,
            industry: o.industry && o.industry.trim() !== '' ? o.industry : '—',
            users: toNumber((o.users ?? o.users_count ?? o.unique_users) as unknown),
            projects: toNumber((o.projects ?? o.projects_count) as unknown),
            avgUsage: toNumber(o.avg_usage),
            totalTokensUsed: toNumber(o.token_usage),
            tokenLimit: undefined,
          }));
        setRows(mapped);
        setTotalCount(
          summaryResp.pagination?.total_count ??
            summaryResp.count ??
            summaryResp.organizations.length
        );

        const topName = summaryResp.summary.top_consumer?.org_name ?? '—';
        const topTokens = toNumber(summaryResp.summary.top_consumer?.token_usage ?? 0);
        setApiSummary({
          totalUsage: toNumber(summaryResp.summary.total_usage),
          activeOrgs: toNumber(summaryResp.summary.active_orgs),
          avgUsagePerOrg: toNumber(summaryResp.summary.avg_usage_per_org),
          topOrgName: topName,
          topOrgTokens: topTokens,
        });
        return;
      }

      const orgs = await organizationService.getOrganizations();
      const fallbackRows = orgs.map(mapOrgToFallbackRow);
      setRows(fallbackRows);
      setTotalCount(fallbackRows.length);
      setApiSummary(null);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, deriveFromTo, industry, listParams]);

  const openDetails = useCallback(
    (row: ITokenUsageRow) => {
      const range = deriveFromTo();
      if (!range) {
        return;
      }
      const { from, to, label } = range;
      navigate(`${ROUTES.TOKEN_USAGE}/${row.orgId}`, {
        state: {
          orgName: row.orgName,
          fromDate: from,
          toDate: to,
          periodLabel: label,
        },
      });
    },
    [deriveFromTo, navigate]
  );

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const token = tokenStorage.getAccessToken();
      if (!token) {
        setLoading(false);
      }
    }
  }, [isAuthenticated, authLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const industries = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => {
      if (r.industry && r.industry !== '—') {
        set.add(r.industry);
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const summary = useMemo(() => {
    if (apiSummary) {
      return {
        totalTokens: apiSummary.totalUsage,
        activeOrgs: apiSummary.activeOrgs,
        tokenPerOrg: apiSummary.avgUsagePerOrg,
        topOrgName: apiSummary.topOrgName,
        topOrgTokens: apiSummary.topOrgTokens,
      };
    }
    const totalTokens = rows.reduce((acc, r) => acc + (r.totalTokensUsed || 0), 0);
    const activeOrgs = rows.filter((r) => (r.totalTokensUsed || 0) > 0).length;
    const tokenPerOrg = rows.length > 0 ? totalTokens / rows.length : 0;
    const topOrg = rows.reduce<ITokenUsageRow | null>((best, r) => {
      if (!best) {
        return r;
      }
      return (r.totalTokensUsed || 0) > (best.totalTokensUsed || 0) ? r : best;
    }, null);
    return {
      totalTokens,
      activeOrgs,
      tokenPerOrg,
      topOrgName: topOrg?.orgName ?? '—',
      topOrgTokens: topOrg?.totalTokensUsed ?? 0,
    };
  }, [rows, apiSummary]);

  const columns = useMemo<ColumnDef<ITokenUsageRow>[]>(
    () => [
      {
        accessorKey: 'orgName',
        header: 'Org Name',
        cell: (info) => (
          <span className='font-normal text-gray-900'>{info.getValue() as string}</span>
        ),
        meta: { width: '20%' },
      },
      { accessorKey: 'industry', header: 'Industry', meta: { width: '14%' } },
      {
        accessorKey: 'users',
        header: 'Users',
        cell: (info) => {
          const v = info.getValue();
          const n = toNumber(v);
          return <span className='text-gray-900'>{v === undefined || v === null ? '—' : n}</span>;
        },
        meta: { width: '10%' },
      },
      {
        accessorKey: 'projects',
        header: 'Projects',
        cell: (info) => {
          const v = info.getValue();
          const n = toNumber(v);
          return <span className='text-gray-900'>{v === undefined || v === null ? '—' : n}</span>;
        },
        meta: { width: '10%' },
      },
      {
        accessorKey: 'avgUsage',
        header: 'Avg Usage',
        cell: (info) => (
          <span className='text-gray-900'>{formatCompactNumber(toNumber(info.getValue()))}</span>
        ),
        meta: { width: '12%' },
      },
      {
        id: 'tokenUsage',
        accessorKey: 'totalTokensUsed',
        header: 'Token Usage',
        cell: (info) => {
          const r = info.row.original;
          const used = r.totalTokensUsed || 0;
          const limit = r.tokenLimit;
          let pctRaw = 0;
          if (limit && limit > 0) {
            pctRaw = used / limit;
          } else if (used > 0) {
            pctRaw = 0.55;
          }
          const pct = clamp01(pctRaw);
          const barClass = getUsageColorClass(pct);
          return (
            <div className='flex items-center gap-3 min-w-0'>
              <div className='h-2.5 w-full max-w-[220px] rounded-full bg-gray-200 overflow-hidden'>
                <div
                  className={`h-full ${barClass}`}
                  style={{ width: `${Math.round(pct * 100)}%` }}
                />
              </div>
              <span className='text-gray-900 font-semibold whitespace-nowrap'>
                {formatCompactNumber(used)}
              </span>
            </div>
          );
        },
        meta: { width: '20%', noTruncate: true },
      },
      {
        id: 'actions',
        header: 'Action',
        cell: (info) => (
          <div className='flex justify-end'>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='p-2! rounded-lg! min-w-0! min-h-0! text-gray-500 hover:text-gray-700 cursor-pointer'
              aria-label='View token usage'
              onClick={() => openDetails(info.row.original)}
            >
              <Eye className='w-5 h-5' />
            </Button>
          </div>
        ),
        meta: { width: '8%', noTruncate: true },
      },
    ],
    [openDetails]
  );

  const customFilters = useMemo(() => {
    const industryOptions = [
      { value: 'all', label: 'All Industries' },
      ...industries.map((opt) => ({ value: opt, label: opt })),
    ];
    const timeRangeOptions = [
      { value: 'last_7_days', label: 'Last 7 days' },
      { value: 'last_30_days', label: 'Last 30 days' },
      { value: 'last_90_days', label: 'Last 90 days' },
      { value: 'custom', label: 'Custom' },
    ];

    return (
      <div className='flex flex-wrap items-center gap-3'>
        <div className='w-full min-w-0 sm:w-56'>
          <SearchableSelect
            value={industry}
            onChange={(v) => setIndustry(v)}
            options={industryOptions}
            placeholder='All Industries'
            className='rounded-lg! py-2! border! border-gray-200!'
            containerClassName='!space-y-0'
          />
        </div>

        <div className='w-full min-w-0 sm:w-48'>
          <SearchableSelect
            value={timeRange}
            onChange={(v) => {
              const next = v as TimeRangeKey;
              setTimeRange(next);
              if (next !== 'custom') {
                setAppliedCustomRange(null);
                setValue('dateFrom', '');
                setValue('dateTo', '');
              }
            }}
            options={timeRangeOptions}
            placeholder='Last 7 days'
            className='rounded-lg! py-2! border! border-gray-200!'
            containerClassName='!space-y-0'
          />
        </div>

        {timeRange === 'custom' ? (
          <DateRangeFilter<ITokenUsageFiltersForm>
            register={register}
            errors={formState.errors}
            dateFromName='dateFrom'
            dateToName='dateTo'
            dateFrom={dateFrom}
            dateTo={dateTo}
            onSearch={() => {
              if (dateFrom && dateTo) {
                setAppliedCustomRange({ from: dateFrom, to: dateTo });
              }
            }}
            onClear={() => {
              setAppliedCustomRange(null);
              setValue('dateFrom', '');
              setValue('dateTo', '');
              setTimeRange('last_7_days');
            }}
            className='shrink-0'
          />
        ) : null}
      </div>
    );
  }, [industry, industries, timeRange, register, formState.errors, dateFrom, dateTo, setValue]);

  return (
    <div className='px-6 pt-3 pb-6 bg-gray-50 min-h-[calc(100vh-4rem)]'>
      <nav className='flex items-center gap-2 mb-4 text-sm'>
        <Link
          to={ROUTES.DASHBOARD}
          className='flex items-center text-theme-500 hover:opacity-80 transition-opacity [&_svg]:stroke-theme-500'
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <path d='M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8'></path>
            <path d='M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
          </svg>
        </Link>
        <span className='text-gray-400 text-base'>›</span>
        <span className='text-gray-500 font-normal'>Token Usage</span>
      </nav>

      <div className='flex justify-between items-center mb-4 flex-wrap gap-4'>
        <div className='flex items-center gap-3 min-w-0 flex-wrap'>
          <h1 className='text-2xl font-bold text-gray-900 m-0'>Token Usage</h1>
          {timeRange === 'custom' && appliedCustomRange ? (
            <span className='px-3 py-1.5 rounded-full bg-white border border-gray-200 text-sm text-gray-700'>
              {selectedRangeLabel}
            </span>
          ) : null}
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5'>
        <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-4'>
          <div className='text-xs text-gray-500 font-semibold'>Total Tokens Used</div>
          <div className='mt-2 text-2xl font-bold text-gray-900'>
            {formatCompactNumber(summary.totalTokens)}
          </div>
          <div className='mt-1 text-sm text-gray-500'>Across all organizations</div>
        </div>
        <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-4'>
          <div className='text-xs text-gray-500 font-semibold'>Active Orgs</div>
          <div className='mt-2 text-2xl font-bold text-gray-900'>{summary.activeOrgs}</div>
          <div className='mt-1 text-sm text-gray-500'>with usage this month</div>
        </div>
        <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-4'>
          <div className='text-xs text-gray-500 font-semibold'>Avg per Org</div>
          <div className='mt-2 text-2xl font-bold text-gray-900'>
            {formatCompactNumber(summary.tokenPerOrg)}
          </div>
          <div className='mt-1 text-sm text-gray-500'>tokens / org</div>
        </div>
        <div className='rounded-2xl border border-gray-100 bg-white shadow-lg p-4'>
          <div className='text-xs text-gray-500 font-semibold'>Top Org</div>
          <div className='mt-2 text-lg font-bold text-gray-900 truncate' title={summary.topOrgName}>
            {summary.topOrgName}
          </div>
          <div className='mt-1 text-sm text-gray-500'>
            {formatCompactNumber(summary.topOrgTokens)} tokens used
          </div>
        </div>
      </div>

      <TanStackTable<ITokenUsageRow>
        data={rows}
        columns={columns}
        loading={authLoading || loading}
        emptyMessage='No token usage found.'
        searchPlaceholder='Search organizations...'
        getRowId={(r) => String(r.orgId || r.orgName)}
        customFilters={customFilters}
        serverSidePagination={true}
        totalItems={totalCount}
        currentPage={page}
        onPageChange={setPage}
        pageSize={pageSize}
        serverSideSearch={true}
        serverSearchValue={search}
        onServerSearchChange={setSearch}
        manualSorting={true}
        onServerSortChange={(sorting) => {
          if (sorting.length > 0) {
            onSortChange(sorting[0].id, sorting[0].desc ? 'desc' : 'asc');
          }
        }}
        resetStateOnDataChange={false}
      />
    </div>
  );
};

export default TokenUsagePage;
